import type {
  BbgBlockEvent,
  BbgTerminalUsage,
  BbgUsageDay,
  BbgUsageMetric,
  BbgUsageResponse,
  HealthStatus,
} from '@shared/contract';

export const USAGE_METRICS: Array<{ id: BbgUsageMetric; label: string }> = [
  { id: 'new_securities', label: 'New securities' },
  { id: 'requests', label: 'Requests' },
  { id: 'hits', label: 'Hits' },
];

export const METRIC_LABEL: Record<BbgUsageMetric, string> = {
  new_securities: 'New securities',
  requests: 'Requests',
  hits: 'Hits',
};

const BLOCK_KIND: Record<string, { label: string; status: HealthStatus }> = {
  bloomberg_limit: { label: 'Bloomberg limit', status: 'down' },
  bloomberg_unhealthy_limit: { label: 'Bloomberg unhealthy limit', status: 'down' },
  gateway_daily_cap: { label: 'Gateway daily cap', status: 'degraded' },
};

export function blockKind(kind: string): { label: string; status: HealthStatus } {
  return BLOCK_KIND[kind] ?? { label: kind, status: 'degraded' };
}

export function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Surge threshold for one metric: max(floor, multiplier × baseline average). */
export function surgeThreshold(
  usage: Pick<BbgUsageResponse, 'surge_floors' | 'surge_multiplier'>,
  baselineAvg: number | null,
  metric: BbgUsageMetric
): number | null {
  const floor = num(usage.surge_floors?.[metric]);
  const multiplier = num(usage.surge_multiplier);
  const scaled = multiplier != null && baselineAvg != null ? multiplier * baselineAvg : null;
  if (floor == null && scaled == null) return null;
  return Math.max(floor ?? 0, scaled ?? 0);
}

export function terminalSurging(terminal: BbgTerminalUsage): boolean {
  return Array.isArray(terminal.surging) && terminal.surging.length > 0;
}

/** Terminals in id order, so terminal-1 always sits left of terminal-2. */
export function terminalEntries(usage: BbgUsageResponse): Array<[string, BbgTerminalUsage]> {
  return Object.entries(usage.terminals ?? {}).sort(([a], [b]) =>
    a.localeCompare(b, 'en', { numeric: true })
  );
}

/** Whole counts: 1,284. */
export function count(value: number | null): string {
  return value == null ? '—' : Math.round(value).toLocaleString('en-US');
}

/** Averages and thresholds: one decimal only when there is one (80.5, 2,400). */
export function average(value: number | null): string {
  if (value == null) return '—';
  return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
}

/** today ÷ baseline, e.g. 0.15× or 3.7×. */
export function ratio(value: number | null): string {
  if (value == null) return '—';
  return `${value.toFixed(value >= 10 ? 0 : value >= 1 ? 1 : 2)}×`;
}

export function ratioOf(value: number | null, baseline: number | null): number | null {
  if (value == null || baseline == null || baseline <= 0) return null;
  return value / baseline;
}

/** 'YYYY-MM-DD' → 'Oct 14'. Parsed as UTC so the label never shifts a day. */
export function dayLabel(date: string): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export function eventTime(ts: string): string {
  const parsed = new Date(ts);
  if (Number.isNaN(parsed.getTime())) return ts;
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function dayValue(day: BbgUsageDay, metric: BbgUsageMetric): number | null {
  return num(day[metric]);
}

/** Contiguous runs of unknown (null) days, as inclusive index ranges. */
export function unknownRuns(values: Array<number | null>): Array<{ start: number; end: number }> {
  const runs: Array<{ start: number; end: number }> = [];
  values.forEach((value, i) => {
    if (value != null) return;
    const last = runs[runs.length - 1];
    if (last && last.end === i - 1) last.end = i;
    else runs.push({ start: i, end: i });
  });
  return runs;
}

/** A clean axis maximum (1/2/2.5/5 × 10^k) and its evenly spaced ticks. */
export function niceScale(max: number, tickCount = 4): { max: number; ticks: number[] } {
  if (!(max > 0)) return { max: 1, ticks: [0, 1] };
  const rawStep = max / tickCount;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step =
    [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rawStep) ?? 10 * magnitude;
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(Number(v.toFixed(6)));
  return { max: top, ticks };
}

/** Axis tick text: 0, 500, 2.5k, 10k. */
export function tickLabel(value: number): string {
  if (value >= 1000) {
    const k = value / 1000;
    return `${Number.isInteger(k) ? k : k.toFixed(1)}k`;
  }
  return String(value);
}

export function eventBaseline(event: BbgBlockEvent): { today: number | null; avg: number | null } {
  return {
    today: num(event.today?.new_securities),
    avg: num(event.baseline_avg?.new_securities),
  };
}
